// frontend/src/components/analyticsMap/CompareLayersPanel.tsx
import {
  Box,
  Flex,
  HStack,
  NativeSelect,
  Slider,
  Switch,
  Text,
  VStack,
  Field,
  type SliderValueChangeDetails,
} from '@chakra-ui/react';
import { METRIC_OPTIONS, UNIT_TYPE_OPTIONS, US_STATES } from '../../types/geoAnalytics'
import type { GeoUnitTypeKey, MetricOption } from '../../types/geoAnalytics'
import type { FilterFieldMetadata, TagOption } from '../../types/contactList'
import type { AreaLayerState, AreaLayerMode } from './AreaLayersPanel'
import { familyYears } from './metricFamilies'
import { LayerModeSwitch } from './LayerModeSwitch'
import { ComparePaneColumn } from './ComparePaneColumn'
import { MetricLegend } from './MetricLegend'
import type { ComparePaneSpec } from './comparePane'

interface CompareLayersPanelProps {
  shared: AreaLayerState
  onSharedChange: (next: AreaLayerState) => void
  allowCompare: boolean
  paneA: ComparePaneSpec
  paneB: ComparePaneSpec
  onPaneAChange: (next: ComparePaneSpec) => void
  onPaneBChange: (next: ComparePaneSpec) => void
  availableMetricKeys: string[]
  fields: FilterFieldMetadata[]
  tags: TagOption[]
  scalesLocked: boolean
  onToggleLock: (checked: boolean) => void
  paneAOption: MetricOption | null
  paneBOption: MetricOption | null
  sharedDomain: [number, number] | null
  scaleAutoComparable: boolean
}

export function CompareLayersPanel({
  shared, onSharedChange, allowCompare, paneA, paneB, onPaneAChange, onPaneBChange,
  availableMetricKeys, fields, tags, scalesLocked, onToggleLock,
  paneAOption, paneBOption, sharedDomain, scaleAutoComparable,
}: CompareLayersPanelProps) {
  // Prefer discovery; fall back to the static catalog so quadrant mode is usable before availableMetricKeys loads.
  const families = familyYears(availableMetricKeys)
  const turnoutStaticYears = METRIC_OPTIONS.find(o => o.key === 'turnout.general')?.yearOptions ?? []
  const turnoutYears = families['turnout.general']?.length ? families['turnout.general'] : turnoutStaticYears
  const latestTurnoutYear = turnoutYears.length > 0 ? turnoutYears[turnoutYears.length - 1] : null

  const handleMode = (m: AreaLayerMode) => {
    if (m === 'quadrant') {
      onSharedChange({ ...shared, mode: 'quadrant', quadrantTurnoutYear: shared.quadrantTurnoutYear ?? latestTurnoutYear })
    } else {
      onSharedChange({ ...shared, mode: m })
    }
  }

  return (
    <VStack gap={3} align="stretch" px={1} py={2}>
      <Field.Root>
        <Field.Label fontSize="xs" mb={1}>State</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            aria-label="State"
            value={shared.state}
            onChange={e => onSharedChange({ ...shared, state: e.target.value })}>
            <option value="">Select a state…</option>
            {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      <Field.Root>
        <Field.Label fontSize="xs" mb={1}>Geography</Field.Label>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            aria-label="Geography"
            value={shared.unitType}
            onChange={e => onSharedChange({ ...shared, unitType: e.target.value as GeoUnitTypeKey })}>
            {UNIT_TYPE_OPTIONS.map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>

      <LayerModeSwitch mode={shared.mode} allowCompare={allowCompare} onModeChange={handleMode} />

      <HStack align="start" gap={3}>
        <ComparePaneColumn title="Pane A" spec={paneA} onChange={onPaneAChange}
          availableMetricKeys={availableMetricKeys} fields={fields} tags={tags} />
        <ComparePaneColumn title="Pane B" spec={paneB} onChange={onPaneBChange}
          availableMetricKeys={availableMetricKeys} fields={fields} tags={tags} />
      </HStack>

      <Field.Root>
        <Field.Label fontSize="xs" mb={1}>Layer opacity: {Math.round(shared.opacity * 100)}%</Field.Label>
        {/* width="full": the vertical Field.Root is align-items:flex-start, so an unsized slider
            shrink-wraps, the track collapses to 0px and only the hollow thumb shows (DT-10, read as
            a "loading spinner"). px keeps the half-overhanging thumb unclipped at 0.1 / 1. */}
        <Slider.Root width="full" px="2" aria-label={["Layer opacity"]} min={0.1} max={1} step={0.05} value={[shared.opacity]}
          onValueChange={(details: SliderValueChangeDetails) => onSharedChange({ ...shared, opacity: details.value[0] })}>
          <Slider.Control>
            <Slider.Track><Slider.Range /></Slider.Track>
            <Slider.Thumb index={0}>
              <Slider.HiddenInput />
            </Slider.Thumb>
          </Slider.Control>
        </Slider.Root>
      </Field.Root>

      <Field.Root display="flex" alignItems="center">
        <Switch.Root id="lock-scales" size="sm" aria-label="Lock color scales" checked={scalesLocked}
          disabled={!scaleAutoComparable}
          onCheckedChange={e => onToggleLock(e.checked === true)} mr={2} >
          <Switch.HiddenInput />
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
        </Switch.Root>
        <Field.Label htmlFor="lock-scales" fontSize="xs" mb={0} color={scaleAutoComparable ? undefined : 'gray.400'}>
          Lock color scales{scaleAutoComparable ? ' (auto)' : ' (not comparable)'}
        </Field.Label>
      </Field.Root>

      {/* Legends: one shared when locked, else one per pane */}
      {scalesLocked && paneAOption && sharedDomain ? (
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Shared scale</Text>
          <MetricLegend option={paneAOption} domain={sharedDomain} />
        </Box>
      ) : (
        <Flex gap={3}>
          {paneAOption && (
            <Box flex={1}><Text fontSize="xs" color="gray.500" mb={1}>Pane A</Text>
              <MetricLegend option={paneAOption} domain={paneAOption.domain} /></Box>
          )}
          {paneBOption && (
            <Box flex={1}><Text fontSize="xs" color="gray.500" mb={1}>Pane B</Text>
              <MetricLegend option={paneBOption} domain={paneBOption.domain} /></Box>
          )}
        </Flex>
      )}
    </VStack>
  );
}
